import React, { useRef, useState } from 'react'
import { Btn, useToast } from '../ui/index.jsx'
import { useLanguage } from '../../i18n/index.jsx'
import { compressImage } from '../../utils/imageCompress.js'
import api from '../../utils/api.js'
import type { User } from '../../types/domain'

interface AvatarUploaderProps {
  user: User | null
  onUserUpdate: (user: User) => void
  onSaveProfile: () => void
  profileLoading: boolean
  profileSuccess: boolean
}

export default function AvatarUploader({ user, onUserUpdate, onSaveProfile, profileLoading, profileSuccess }: AvatarUploaderProps) {
  const { show } = useToast()
  const { t } = useLanguage()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [avatarUploading, setAvatarUploading] = useState(false)
  const [avatarBroken, setAvatarBroken] = useState(false)

  const handlePhotoSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    setAvatarUploading(true)
    const compressed = await compressImage(file)
    const formData = new FormData()
    formData.append('photo', compressed)

    try {
      const res = await api.post('/users/me/avatar', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      const data = res.data as { avatar_url: string }
      onUserUpdate({ ...user, avatar_url: data.avatar_url } as User)
      setAvatarBroken(false)
      show(t('photoUpdated'))
    } catch {
      show(t('photoUploadFailed'), 'error')
    } finally {
      setAvatarUploading(false)
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', paddingBottom: '1rem', borderBottom: '1px solid var(--border-subtle)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
        {user?.avatar_url && !avatarBroken ? (
          <img
            src={user.avatar_url}
            alt="Avatar"
            style={{
              width: 40, height: 40, borderRadius: '50%',
              objectFit: 'cover', border: '2px solid var(--border-default)'
            }}
            onError={() => setAvatarBroken(true)}
          />
        ) : (
          <div style={{
            width: 40, height: 40, borderRadius: '50%',
            background: 'var(--accent)', color: 'var(--text-inverse)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontWeight: 600, fontSize: 'var(--text-lg)'
          }}>
            {user?.username?.[0]?.toUpperCase() || 'U'}
          </div>
        )}
        <div style={{ flex: 1 }}>
          <p style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{user?.username}</p>
          <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text-muted)' }}>
            {user?.work_title || user?.role}
          </p>
        </div>
      </div>
      <div style={{ marginTop: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          onChange={handlePhotoSelect}
          aria-label={t('changePhoto')}
          style={{ display: 'none' }}
        />
        <Btn
          variant="ghost"
          size="sm"
          onClick={() => { fileInputRef.current?.click() }}
          disabled={avatarUploading}
          style={{ fontSize: 'var(--text-sm)' }}
        >
          {avatarUploading ? t('uploading') : t('changePhoto')}
        </Btn>
        <Btn
          variant="primary"
          size="sm"
          onClick={onSaveProfile}
          loading={profileLoading}
          style={{ fontSize: 'var(--text-sm)' }}
        >
          {profileSuccess ? `✓ ${t('saved')}` : t('save')}
        </Btn>
      </div>
    </div>
  )
}
